import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { mustGetAccountPlan } from '@/data/account/selectors';
import type { PendingSubmissionData } from '@/rippling/hooks/useRipplingDemo';
import {
  extractUserData,
  formatPhoneNumberForRippling,
  getEmployeeCount,
  mapGooglePlaceCountryToRipplingCountry,
} from '@/rippling/util/ripplingDataHelpers';
import { useRipplingXsConfig } from '@/rippling/util/ripplingXsUtils';
import {
  type RipplingDemoTimePayload,
  type RipplingPayloadWrapper,
  sendRipplingDemoTimeEvent,
} from '@/rippling/util/sendRipplingEvent';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import { getPreviousPath } from '@/shared/router/middleware/pageViewTracker';
import SpinningLoader from '@/shared/ui/loaders/SpinningLoader';
import Environment from '@/shared/util/environment';
import { posthogCapture } from '@/shared/vendor/posthog';
import { useWiwSelector } from '@/store';

import { Overlay, Portal } from '@mantine/core';

import '../assets/styles/RipplingDemoOverlay.scss';

/**
 * Extracts UTM parameters from the current page URL and maps them to Rippling payload field names.
 *
 * @returns Object containing UTM parameters that are present in the current URL
 */
function extractUtmParameters(): {
  LT_Campaign__c?: string;
  LT_Content__c?: string;
  LT_Medium__c?: string;
  LT_Source__c?: string;
  LT_Term__c?: string;
} {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  // Map URL UTM parameters to Rippling payload field names
  // These field names match the expected format for the Rippling Growth API
  const utmMapping = {
    utm_campaign: 'LT_Campaign__c',
    utm_content: 'LT_Content__c',
    utm_medium: 'LT_Medium__c',
    utm_source: 'LT_Source__c',
    utm_term: 'LT_Term__c',
  };

  Object.entries(utmMapping).forEach(([urlParam, payloadField]) => {
    const value = urlParams.get(urlParam);
    if (value) {
      utmParams[payloadField] = value;
    }
  });

  return utmParams;
}

/**
 * UTM parameters that can be passed to override URL-based UTM params
 */
interface UtmParamsOverride {
  utm_campaign?: string;
  utm_content?: string;
  utm_medium?: string;
  utm_source?: string;
  utm_term?: string;
}

interface RipplingDemoOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Optional queue name override. If provided, bypasses the default logic
   * that determines queue based on employee count.
   * Example values: "WiW SB", "WiW MM", "WiW ENT", "WiW"
   */
  queueNameOverride?: string;
  /**
   * Optional lead routing ID override for Salesforce.
   * If not provided, uses the value from LaunchDarkly config or default.
   */
  leadRoutingOverride?: string;
  /**
   * Optional UTM parameters override. If provided, these take precedence
   * over UTM parameters extracted from the page URL.
   */
  utmParams?: UtmParamsOverride;
  /**
   * When true, phone collection will be triggered after a successful booking
   * if the user doesn't have a phone number on file.
   */
  requirePhoneCollection?: boolean;
  /**
   * Callback invoked when a booking is confirmed and phone collection is needed.
   * Receives the complete payload data to be submitted after phone collection.
   */
  onBookingNeedsPhone?: (data: PendingSubmissionData) => void;
  /**
   * When true, waits briefly for Growth API submission before closing.
   * Useful for webview/new-tab flows that may terminate in-flight requests on close.
   */
  waitForGrowthSubmitBeforeClose?: boolean;
  /**
   * Maximum time to wait for Growth submission before closing when
   * waitForGrowthSubmitBeforeClose is enabled.
   */
  growthSubmitCloseTimeoutMs?: number;
  /**
   * Optional callback to render a back button inside the overlay.
   * When provided, a back button is displayed at the top-left of the overlay.
   */
  onBack?: () => void;
}

interface RipplingEventPayload {
  [key: string]: unknown;
}

/**
 * Determines the appropriate Rippling queue name based on employee count and country.
 * Routes customers to different sales queues (Small Business, Mid-Market, Enterprise, or WiW)
 * based on their company size and special country rules.
 *
 * @param employeeCount - Number of employees in the account
 * @param headquarters - Country name for headquarters
 * @param config - Configuration object containing queue names for different business sizes
 * @returns The queue name string or null if employee count is invalid
 */
function getWiWQueueName(
  employeeCount: number | null | undefined,
  headquarters: string | null,
  config: {
    smallBusinessQueueName?: string;
    midMarketQueueName?: string;
    enterpriseQueueName?: string;
  },
): string | null {
  if (employeeCount === null || employeeCount === undefined || employeeCount < 0) {
    return null;
  }

  // Special cases for New Zealand and Singapore - always use WiW regardless of size
  if (headquarters === 'New Zealand' || headquarters === 'Singapore') {
    return 'WiW';
  }

  if (employeeCount >= 0 && employeeCount <= 50) {
    return config.smallBusinessQueueName || 'WiW SB';
  }

  if (employeeCount >= 51 && employeeCount <= 250) {
    return config.midMarketQueueName || 'WiW MM';
  }

  if (employeeCount >= 251) {
    return config.enterpriseQueueName || 'WiW ENT';
  }

  return null;
}

/**
 * Toggle button for testing UAT vs Production Rippling iframe.
 * Only visible in non-production environments.
 */
function EnvironmentToggleButton({ useProduction, onToggle }: { useProduction: boolean; onToggle: () => void }) {
  if (Environment.isProduction()) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={e => {
        e.stopPropagation();
        onToggle();
      }}
      style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 1000,
        padding: '8px 12px',
        background: useProduction ? '#28a745' : '#ffc107',
        color: useProduction ? 'white' : 'black',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold',
      }}
    >
      {useProduction ? 'PROD' : 'UAT'}
    </button>
  );
}

export default function RipplingDemoOverlay({
  isOpen,
  onClose,
  queueNameOverride,
  leadRoutingOverride,
  utmParams: utmParamsOverride,
  requirePhoneCollection,
  onBookingNeedsPhone,
  waitForGrowthSubmitBeforeClose = false,
  growthSubmitCloseTimeoutMs = 1500,
  onBack,
}: RipplingDemoOverlayProps) {
  const user = useWiwSelector(getAuthUser);
  const account = useWiwSelector(getAuthAccount);
  const accountPlan = useWiwSelector(mustGetAccountPlan);
  const ripplingConfig = useRipplingXsConfig();

  const { smallBusinessQueueName, midMarketQueueName, enterpriseQueueName, leadRouting } = ripplingConfig;
  // Use override if provided, otherwise fall back to config value
  const effectiveLeadRouting = leadRoutingOverride || leadRouting || '701Ud00000SyLMVIA3';
  const [isLoaded, setIsLoaded] = useState(false);
  // Toggle for testing UAT vs Production iframe (only visible in non-production)
  const [useProduction, setUseProduction] = useState(Environment.isProduction());
  const [flowState, setFlowState] = useState({
    demotimeEligible: true,
    demotimeLoaded: false,
    demotimeClosed: false,
    didMeetingAvailabiltyLoad: false,
  });

  // Capture referrer and current URL once when modal opens
  const referrerUrl = useRef<string>('');
  const currentUrl = useRef<string>('');
  // Track whether we've deferred submission for phone collection (to skip idle/close submissions)
  const hasDeferredForPhoneCollection = useRef(false);
  const submitToGrowthAndClose = useCallback(
    async (payload: RipplingPayloadWrapper) => {
      if (!waitForGrowthSubmitBeforeClose) {
        // Keep current behavior in standard in-app flows.
        void sendRipplingDemoTimeEvent(payload);
        onClose();
        return;
      }

      // In webviews/new tabs, immediate close can terminate in-flight requests.
      // Give the Growth submission a short window before closing.
      await Promise.race([
        sendRipplingDemoTimeEvent(payload),
        new Promise(resolve => setTimeout(resolve, growthSubmitCloseTimeoutMs)),
      ]);
      onClose();
    },
    [onClose, waitForGrowthSubmitBeforeClose, growthSubmitCloseTimeoutMs],
  );

  useEffect(() => {
    if (isOpen && !currentUrl.current) {
      // Capture URLs only on first open
      currentUrl.current = window.location.href;

      // Get the previous page pathname from sessionStorage
      const previousPath = getPreviousPath();
      if (previousPath) {
        // Convert pathname to full URL
        referrerUrl.current = `${window.location.origin}${previousPath}`;
      } else {
        // Fallback to document.referrer for external referrers
        referrerUrl.current = document.referrer || '';
      }
    }
  }, [isOpen]);

  /**
   * Builds the complete payload for submission to the Growth API.
   */
  const buildPayload = useCallback(
    (eventData: Record<string, unknown>, updatedFlowState: typeof flowState): RipplingPayloadWrapper => {
      const isTrial = accountPlan.isTrial() || accountPlan.isReverseTrial();

      // Get Google Place country code and map it to full country name
      const place = account?.hasPlace() ? account.getPlace() : null;
      const googlePlaceCountryCode = place?.country;
      const mappedCountry = mapGooglePlaceCountryToRipplingCountry(googlePlaceCountryCode);
      const country = mappedCountry || 'United States';

      const userData = extractUserData(user, account, isTrial, country);
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Get UTM params from URL, then override with any configured params
      const urlUtmParams = extractUtmParameters();
      const utmParams: Record<string, string> = { ...urlUtmParams };

      // Apply overrides from config if provided
      if (utmParamsOverride) {
        if (utmParamsOverride.utm_campaign) {
          utmParams.LT_Campaign__c = utmParamsOverride.utm_campaign;
        }
        if (utmParamsOverride.utm_content) {
          utmParams.LT_Content__c = utmParamsOverride.utm_content;
        }
        if (utmParamsOverride.utm_medium) {
          utmParams.LT_Medium__c = utmParamsOverride.utm_medium;
        }
        if (utmParamsOverride.utm_source) {
          utmParams.LT_Source__c = utmParamsOverride.utm_source;
        }
        if (utmParamsOverride.utm_term) {
          utmParams.LT_Term__c = utmParamsOverride.utm_term;
        }
      }

      const isBooking = Object.keys(eventData).length > 0;
      const landingPagePath = new URL(currentUrl.current).pathname;

      const payload = {
        ...userData,
        formFillLandingPage: currentUrl.current,
        Form_Fill_Referring_URL__c: referrerUrl.current,
        ...updatedFlowState,
        clientTimeZone,
        demotimeEvents: JSON.stringify(eventData),
        // UTM parameters from current page
        ...utmParams,
        // System-generated tracking fields
        Form_Type__c: 'Demo Request',
        Landing_Page__c: landingPagePath,
        formProgress_stage2: 'true',
        transactionId: crypto.randomUUID(),
        formIdentifier: 'wiw-demo-request',
        Last_Lead_Origin__c: effectiveLeadRouting,
        user_agent: navigator.userAgent,
      } as RipplingDemoTimePayload;

      // In non-production environments, include OpenpriseEnabled flag based on toggle state
      if (!Environment.isProduction()) {
        payload.OpenpriseEnabled = useProduction;
      }

      if (isBooking) {
        payload.ActivityDate = eventData.meetingStartDateTimeUTC as string | undefined;
        payload.ActivityDateTime = eventData.meetingStartDateTimeUTC as string | undefined;
        payload.ActivityDuration = eventData.meetingDuration as number | undefined;
        payload.ActivityTimeZone = eventData.meetingLocalTimezone as string | undefined;
        payload.EventStartDateTime = eventData.meetingStartDateTimeUTC as string | undefined;
        payload.EventEndDateTime = eventData.meetingEndDateTimeUTC as string | undefined;
        payload.AssigneeEmail = eventData.assigneeEmail as string | undefined;
        payload.AssigneeId = eventData.assigneeId as string | undefined;
        payload.EventOwnerId = eventData.assigneeUserId as string | undefined;
        payload.DemotimeMeetingId = eventData.meetingId as string | undefined;
        payload.Queue_Name_CP__c = eventData.routingId as string | undefined;
        payload.Router_Name_CP__c = eventData.routingName as string | undefined;
        payload.dt_Meeting_Datetime_In_Prospect_Time_Zone = eventData.dt_Meeting_Datetime_In_Prospect_Time_Zone as
          | string
          | undefined;
        payload.dt_Meeting_Datetime_String_In_Prospect_Time_Zone =
          eventData.dt_Meeting_Datetime_String_In_Prospect_Time_Zone as string | undefined;
      }

      return { data: payload };
    },
    [user, account, accountPlan, effectiveLeadRouting, utmParamsOverride, useProduction],
  );

  const iframeUrl = useMemo(() => {
    // In non-production, allow toggling between UAT and Production iframe
    // In production, always use production iframe
    const baseUrl =
      !Environment.isProduction() && !useProduction
        ? 'https://rippling--uat.sandbox.my.site.com/meetings/embedded-queue'
        : 'https://meetings.rippling.com/embedded-queue';
    const params = new URLSearchParams();

    if (user?.email) {
      params.append('work_email_address', user.email);
    }
    if (user?.first_name && user?.last_name) {
      params.append('Full_Name', `${user.first_name} ${user.last_name}`);
    }
    if (user?.phone_number) {
      params.append('Phone', formatPhoneNumberForRippling(user.phone_number, user.country_code));
    }

    if (account?.company) {
      params.append('Company_Name', account.company);
    }

    if (account?.id) {
      params.append('WIW_ID', account.id.toString());
    }

    const isTrial = accountPlan.isTrial() || accountPlan.isReverseTrial();
    const employeeCount = getEmployeeCount(account, isTrial);

    // Get Google Place country code and map it to Rippling accepted country
    const place = account?.hasPlace() ? account.getPlace() : null;
    const googlePlaceCountryCode = place?.country;
    const mappedCountry = mapGooglePlaceCountryToRipplingCountry(googlePlaceCountryCode);

    // Determine headquarters value - use mapped country or fallback to "United States"
    const headquarters = mappedCountry || 'United States';

    if (employeeCount > 0) {
      params.append('Number_of_employees', employeeCount.toString());
    }

    // Use queue name override if provided, otherwise calculate based on employee count
    if (queueNameOverride) {
      params.append('Product', queueNameOverride);
    } else if (employeeCount > 0) {
      const queueName = getWiWQueueName(employeeCount, mappedCountry, {
        smallBusinessQueueName,
        midMarketQueueName,
        enterpriseQueueName,
      });
      if (queueName) {
        params.append('Product', queueName);
      }
    }

    params.append('Headquarters', headquarters);
    params.append('mappedClientTimeZone', Intl.DateTimeFormat().resolvedOptions().timeZone);

    if (!Environment.isProduction()) {
      params.append('debug', 'true');
    }

    const queryString = params.toString().replaceAll('+', '%20');
    return `${baseUrl}?${queryString}`;
  }, [
    user,
    account,
    accountPlan,
    smallBusinessQueueName,
    midMarketQueueName,
    enterpriseQueueName,
    queueNameOverride,
    useProduction,
  ]);

  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      const { eventName, payload } = event.data as {
        eventName?: string;
        payload?: RipplingEventPayload;
      };

      if (!eventName) {
        return;
      }

      switch (eventName) {
        case 'availability-loaded':
          posthogCapture('rippling_demo_time.availability_loaded');
          setFlowState(prev => ({
            ...prev,
            demotimeLoaded: true,
            didMeetingAvailabiltyLoad: true,
          }));
          break;

        case 'booking-confirmed': {
          posthogCapture('rippling_demo_time.booking_confirmed', payload);
          const updatedFlowState = {
            ...flowState,
            demotimeEligible: true,
            demotimeLoaded: true,
            didMeetingAvailabiltyLoad: true,
            demotimeClosed: false,
          };
          setFlowState(updatedFlowState);

          const wrappedPayload = buildPayload(payload || {}, updatedFlowState);

          // Check if we need to collect phone after booking
          const needsPhone = requirePhoneCollection && !user.phone_number;
          if (needsPhone && onBookingNeedsPhone) {
            // Defer submission - pass the complete payload data to be submitted later
            hasDeferredForPhoneCollection.current = true;
            onBookingNeedsPhone({ payload: wrappedPayload });
            onClose();
          } else {
            // Submit immediately
            await submitToGrowthAndClose(wrappedPayload);
          }
          break;
        }

        case 'idle':
        case 'close': {
          posthogCapture(`rippling_demo_time.${eventName}`);
          // Skip submission if we've already deferred for phone collection
          if (!hasDeferredForPhoneCollection.current) {
            const updatedFlowState = {
              ...flowState,
              demotimeEligible: true,
              demotimeClosed: true,
            };
            setFlowState(updatedFlowState);
            const wrappedPayload = buildPayload({}, updatedFlowState);
            await submitToGrowthAndClose(wrappedPayload);
          } else {
            onClose();
          }
          break;
        }

        case 'no_queue_found':
          posthogCapture('rippling_demo_time.no_queue_found');
          break;
      }
    },
    [
      onClose,
      buildPayload,
      requirePhoneCollection,
      onBookingNeedsPhone,
      user.phone_number,
      flowState,
      submitToGrowthAndClose,
    ],
  );

  useEffect(() => {
    setFlowState({
      demotimeEligible: isOpen,
      demotimeLoaded: false,
      demotimeClosed: false,
      didMeetingAvailabiltyLoad: false,
    });
    // Reset the deferred flag only when overlay opens
    if (isOpen) {
      hasDeferredForPhoneCollection.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsLoaded(false);
      return;
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isOpen, handleMessage]);

  if (!isOpen) {
    return null;
  }

  return (
    <Portal>
      <Overlay backgroundOpacity={0.5} onClick={onClose} className="rippling-demo-overlay" zIndex={1200}>
        <EnvironmentToggleButton
          useProduction={useProduction}
          onToggle={() => {
            setUseProduction(!useProduction);
            setIsLoaded(false);
          }}
        />
        {onBack && (
          <button
            type="button"
            className="rippling-demo-back-btn"
            onClick={e => {
              e.stopPropagation();
              onBack();
            }}
          >
            &larr; Back
          </button>
        )}
        {!isLoaded && (
          <div className="rippling-demo-spinner">
            <SpinningLoader width={40} height={40} color="white" />
          </div>
        )}
        <iframe
          src={iframeUrl}
          title="Schedule a Demo with Rippling"
          referrerPolicy="no-referrer-when-downgrade"
          className={`rippling-demo-iframe ${isLoaded ? 'loaded' : 'loading'}`}
          onLoad={() => setIsLoaded(true)}
        />
      </Overlay>
    </Portal>
  );
}
